import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ShieldCheck,
  Lock,
  ArrowRight,
  Quote,
  Compass,
  ListChecks,
  FileText,
  PenLine,
  ShieldCheck as ShieldCheck2,
  Reply,
  Plus,
  AtSign,
  Globe,
  Mail,
} from "lucide-react";

/* =========================================================================
   Landing — 1:1 port of the `Landing` component in design/conversion-flow.jsx.
   Public marketing page; full-page (not inside the prototype's fixed-size
   .app card). The design system (.btn-primary, .pill, .eyebrow, .faq-acc,
   .marquee-track, .bleed, .fade, .rise, .glow-ring, etc.) is already global.
   ========================================================================= */

const TESTIMONIALS = [
  {
    n: "Punna Reddy Ullapu, Ph.D.",
    t: "Lead Scientist",
    img: "/images/t-punna.png",
    q: "Excellent scientific knowledge along with immigration filings. He drafted my petition from scratch in a short period and replied to all my queries professionally. Strongly recommend for extraordinary-ability filings.",
  },
  {
    n: "Raja Reddy BommaReddy, Ph.D.",
    t: "Principal Scientist",
    img: "/images/t-raja.png",
    q: "A transformative experience. Despite an RFE, his meticulous prep led to approval. He prepared my petition in 3–4 weeks; the whole process took only four months — a timeline I never imagined possible.",
  },
  {
    n: "Balawanthrao Jadhav",
    t: "Associate Analytical Scientist",
    img: "/images/t-balawanthrao.png",
    q: "Outstanding job filing my EB1A and O-1 in premium. I got approval of both because of the quality of the petition. One-on-one Zoom meetings and prompt responses gave me peace of mind.",
  },
  {
    n: "Rahul Gauttam",
    t: "Senior Scientist & Program Lead",
    img: "/images/t-rahul.png",
    q: "Phenomenal job on my EB1A. Quick to schedule meetings and equally quick preparing the petition. He took just 30 days to a final draft to my satisfaction. Highly recommend.",
  },
  {
    n: "Adithya Peddapuram",
    t: "Senior Scientist III, Cambrex",
    img: "/images/t-adithya.png",
    q: "Outstanding job drafting my EB1A. Regular meetings to keep me updated, quickly grasped my research area, and prepared the petition promptly and efficiently.",
  },
  {
    n: "Ramu Kakumanu",
    t: "Bioanalytical Chemist",
    img: "/images/t-ramu.png",
    q: "Phenomenal job on my O1A approval — timely filing and a smooth H1B to O1A transition. My EB1A was filed after a great review of my publications and citations.",
  },
  {
    n: "Disha Gandhi",
    t: "Medicinal Chemist",
    img: "/images/t-disha.png",
    q: "He reviewed, corrected, and reformatted my petition, advised two more criteria, and helped draft both. We finished in 30 days — very commendable for such a time-consuming process.",
  },
];

function TCard({ t }: { t: (typeof TESTIMONIALS)[number] }) {
  return (
    <div
      className="rounded-2xl p-4 shrink-0 flex flex-col"
      style={{
        width: 300,
        background: "var(--surface)",
        border: "1px solid var(--border)",
      }}
    >
      <Quote className="w-4 h-4 text-terra-500 shrink-0" />
      <p className="text-[12.5px] text-ink-1 leading-relaxed mt-1.5 flex-1">{t.q}</p>
      <div
        className="flex items-center gap-2.5 mt-3 pt-3"
        style={{ borderTop: "1px solid var(--hair)" }}
      >
        <img
          src={t.img}
          alt={t.n}
          className="w-8 h-8 rounded-full object-cover shrink-0"
          style={{ border: "1px solid var(--border)" }}
        />
        <div className="min-w-0">
          <div className="text-[12.5px] font-semibold text-ink leading-tight truncate">{t.n}</div>
          <div className="text-[12.5px] text-ink-3 truncate">{t.t}</div>
        </div>
      </div>
    </div>
  );
}

function FAQItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: "1px solid var(--border)" }}>
      <button
        className="w-full flex items-center gap-4 py-4 text-left focus-ring"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className="flex-1 text-[16px] font-semibold text-ink">{q}</span>
        <span className="faq-chev shrink-0 text-ink-3" data-open={open}>
          <Plus className="w-4 h-4" />
        </span>
      </button>
      <div className="faq-acc" data-open={open}>
        <div>
          <p className="text-[14px] text-ink-2 leading-relaxed pb-4 pr-8">{a}</p>
        </div>
      </div>
    </div>
  );
}

function LandingHeader() {
  return (
    <div
      className="flex items-center justify-between px-7 h-[64px] shrink-0"
      style={{
        background: "var(--surface)",
        borderBottom: "1px solid var(--border)",
      }}
    >
      <div className="flex items-center gap-2.5">
        <span
          className="w-9 h-9 rounded-xl flex items-center justify-center text-white"
          style={{ background: "linear-gradient(140deg,var(--success),var(--forest))" }}
        >
          <ShieldCheck className="w-[18px] h-[18px]" />
        </span>
        <div className="leading-tight">
          <div className="font-display text-[20px] text-ink leading-none">VisaGenius</div>
        </div>
      </div>
      <span />
      <div className="flex items-center gap-2">
        <span className="flex items-center gap-3">
          <Link
            to="/expert"
            className="hidden sm:inline-flex items-center h-10 px-4 text-[14px] font-medium text-ink-1 quiet-link focus-ring"
            style={{ textDecoration: "none" }}
          >
            Talk to an expert
          </Link>
          <Link
            to="/sign-in"
            className="focus-ring rounded-lg h-10 px-5 inline-flex items-center text-[14px] font-semibold text-ink-1 whitespace-nowrap"
            style={{ border: "1.5px solid var(--ring-muted, #cbb993)", textDecoration: "none" }}
          >
            Log in
          </Link>
        </span>
      </div>
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const [showSticky, setShowSticky] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const next = () => navigate("/scholar-path");

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: "var(--surface)", color: "var(--ink)" }}
    >
      <LandingHeader />
      <div
        ref={scrollRef}
        className="flex-1 min-h-0 overflow-y-auto nice-scroll"
        onScroll={(e) =>
          setShowSticky((e.target as HTMLDivElement).scrollTop > 360)
        }
      >
        {showSticky && (
          <button
            onClick={next}
            className="focus-ring arrow-nudge fixed z-40 inline-flex items-center gap-2 rounded-full px-6 h-[52px] text-[15px] font-semibold text-white shadow-lg"
            style={{
              background: "var(--accent)",
              bottom: 24,
              left: "50%",
              transform: "translateX(-50%)",
              boxShadow: "0 14px 34px -12px rgba(176,78,52,0.6)",
            }}
          >
            Check eligibility — free <ArrowRight className="w-4 h-4" />
          </button>
        )}
        <div className="max-w-[980px] mx-auto px-9 py-10 fade">
          {/* HERO */}
          <div className="grid grid-cols-[1.02fr_0.98fr] gap-12 items-center min-h-[560px]">
            <div>
              <h1
                className="font-display text-[clamp(46px,6.4vw,76px)] leading-[1.02] tracking-[-0.012em] text-ink rise rise-2"
                style={{ textWrap: "balance" as React.CSSProperties["textWrap"] }}
              >
                Your <span style={{ whiteSpace: "nowrap" }}>EB-1A</span> journey{" "}
                <span style={{ color: "var(--accent)" }}>starts here.</span>
              </h1>
              <p className="mt-6 text-[17px] text-ink-2 leading-relaxed max-w-[480px] rise rise-3">
                Build your EB-1A case with confidence. Whether you’re a researcher,
                engineer, scientist, or academic, VisaGenius provides the legal
                expertise and personalized support to petition for yourself — no
                employer or sponsor needed.
              </p>
              <div className="mt-8 rise rise-4">
                <button
                  className="btn-primary focus-ring arrow-nudge h-14 px-8 text-[17px] inline-flex items-center gap-2"
                  onClick={next}
                >
                  Check eligibility <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* hero visual — layered frame */}
            <div className="relative rise rise-3">
              <div
                className="rounded-[26px] p-3.5 float-a glow-ring"
                style={{
                  background: "var(--soft)",
                  border: "1px solid var(--border)",
                  boxShadow: "0 40px 90px -50px rgba(31,29,24,0.5)",
                }}
              >
                <div className="rounded-[18px] overflow-hidden">
                  <img
                    src="/images/hero-couple.png"
                    alt="A couple embracing"
                    style={{ display: "block", width: "100%", height: 500, objectFit: "cover" }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* THE VISAGENIUS DIFFERENCE — sage-50 full-bleed band */}
          <div
            className="bleed px-9 py-16 mt-16"
            style={{
              background: "var(--success-50)",
              borderTop: "1px solid #cdd6bd",
              borderBottom: "1px solid #cdd6bd",
            }}
          >
            <div className="max-w-[980px] mx-auto grid grid-cols-[0.85fr_1.15fr] gap-12 items-start">
              <div>
                <span
                  className="eyebrow"
                  style={{ color: "var(--success-700)", borderColor: "#cdd6bd" }}
                >
                  What sets us apart
                </span>
                <div className="font-display text-[40px] leading-[1.06] text-ink mt-4">
                  The VisaGenius difference.
                </div>
                <p className="text-[14px] leading-relaxed mt-4 text-ink-2">
                  Built by people who’ve guided dozens of researchers to approval —
                  and who treat your case like it’s the only one that matters.
                </p>
              </div>
              <div>
                {[
                  [
                    "Proven approvals",
                    "A track record of approved EB-1A and O-1 petitions — including cases that overcame an RFE.",
                  ],
                  [
                    "Deep EB-1A expertise",
                    "We focus on extraordinary-ability cases. We know exactly how USCIS reads each of the eight criteria.",
                  ],
                  [
                    "No employer needed",
                    "You petition for yourself. No sponsor, no job offer, no strings attached to your green card.",
                  ],
                  [
                    "White-glove support",
                    "One-on-one guidance, fast responses, and an attorney who reviews every page before you file.",
                  ],
                ].map(([t, d], i) => (
                  <div
                    key={t}
                    className="flex gap-5 py-4"
                    style={{ borderTop: i ? "1px solid #cdd6bd" : "none" }}
                  >
                    <span
                      className="font-display text-[26px] leading-none shrink-0"
                      style={{ color: "var(--accent)" }}
                    >
                      {i + 1}
                    </span>
                    <div>
                      <div className="text-[16px] font-semibold text-ink">{t}</div>
                      <div className="text-[13.5px] leading-relaxed mt-1 text-ink-2">{d}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>


          {/* TESTIMONIALS MARQUEE */}
          <div className="mt-16">
            <span className="eyebrow">Approved researchers</span>
            <div className="font-display text-[34px] leading-[1.1] text-ink mt-3 mb-4">
              Researchers we’ve helped get approved.
            </div>
            <div
              className="bleed marquee-mask"
              style={{
                overflow: "hidden",
                maskImage:
                  "linear-gradient(90deg,transparent,#000 6%,#000 94%,transparent)",
                WebkitMaskImage:
                  "linear-gradient(90deg,transparent,#000 6%,#000 94%,transparent)",
              }}
            >
              <div className="marquee-track px-4">
                {[...TESTIMONIALS, ...TESTIMONIALS].map((t, i) => (
                  <TCard key={i} t={t} />
                ))}
              </div>
            </div>
          </div>

          {/* SERVICES */}
          <div className="mt-16">
            <span className="eyebrow">What sets us apart</span>
            <div className="font-display text-[34px] leading-[1.1] text-ink mt-3 mb-1">
              What VisaGenius does for you.
            </div>
            <div className="text-[14px] text-ink-2 mb-5">
              Expert-led, end to end — every part of your petition handled with you.
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                {
                  Icon: Compass,
                  t: "Personalized legal strategy",
                  d: "Work with our immigration attorneys to pinpoint the EB-1A criteria that best fit your background, and shape a clear case plan from day one.",
                },
                {
                  Icon: ListChecks,
                  t: "Custom evidence review + checklist",
                  d: "We review your credentials and build a tailored checklist that guides your evidence gathering — no guessing, no confusion.",
                },
                {
                  Icon: FileText,
                  t: "Form I-140 preparation",
                  d: "Our team prepares your Form I-140 and supporting materials with precision and full compliance to USCIS standards.",
                },
                {
                  Icon: PenLine,
                  t: "Letter drafting support",
                  d: "We help you craft compelling personal statements and recommendation letters that reinforce your EB-1A qualifications.",
                },
                {
                  Icon: ShieldCheck2,
                  t: "In-depth attorney review",
                  d: "An experienced immigration attorney reviews every part of your petition to catch issues early and maximize your odds of approval.",
                },
                {
                  Icon: Reply,
                  t: "RFE response guidance",
                  d: "If USCIS issues a Request for Evidence, we help you respond with a strong, well-supported legal rebuttal.",
                },
              ].map((s) => (
                <div
                  key={s.t}
                  className="rounded-2xl p-5 flex gap-3.5"
                  style={{
                    background: "var(--surface)",
                    border: "1px solid var(--border)",
                  }}
                >
                  <span
                    className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                    style={{
                      background: "var(--accent-50)",
                      color: "var(--accent-700)",
                    }}
                  >
                    <s.Icon className="w-5 h-5" />
                  </span>
                  <div>
                    <div className="text-[14.5px] font-semibold text-ink leading-tight">
                      {s.t}
                    </div>
                    <div className="text-[12.5px] text-ink-2 mt-1.5 leading-relaxed">
                      {s.d}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* FAQ */}
          <div className="mt-16">
            <span className="eyebrow">Frequently asked questions</span>
            <div className="font-display text-[34px] leading-[1.1] text-ink mt-3 mb-4">
              Got questions? We’re on it.
            </div>
            <div>
              {[
                [
                  "Do I need an employer to sponsor me?",
                  "No. EB-1A is self-petitioned — you file for yourself with no employer, job offer, or labor certification. Your green card stays with you, not a company.",
                ],
                [
                  "How do I know if I qualify?",
                  "You need a major international award, or to meet at least 3 of the 8 EB-1A criteria (awards, memberships, published material, judging, original contributions, scholarly authorship, leading role, or high salary). Our free assessment reads your record and shows you where you stand in about 2 minutes.",
                ],
                [
                  "How long does the process take?",
                  "Most petitions are ready to file in 4–6 weeks. With premium processing, USCIS decides your I-140 within 15 business days; standard processing takes several months.",
                ],
                [
                  "What does VisaGenius actually do for me?",
                  "We build your case end to end — strategy, a custom evidence checklist, your recommendation letters, the Form I-140, every exhibit, and the petition brief — all reviewed by an immigration attorney before you file.",
                ],
                [
                  "What if I get a Request for Evidence (RFE)?",
                  "We help you respond with a strong, well-supported legal rebuttal. Several of our approvals came after successfully overcoming an RFE.",
                ],
              ].map(([q, a]) => (
                <FAQItem key={q} q={q} a={a} />
              ))}
            </div>
          </div>

          {/* FULL-BLEED RIBBON */}
          <div
            className="bleed px-9 py-16 mt-16 flex flex-col items-center justify-center gap-6 text-center"
            style={{
              background: "var(--success)",
              borderTop: "4px solid var(--success-700)",
              borderBottom: "4px solid var(--success-700)",
            }}
          >
            <span
              className="font-display text-[clamp(40px,6vw,64px)] leading-[1.04] tracking-[-0.015em]"
              style={{ color: "#1f1d18" }}
            >
              Find your EB-1A path in 2 minutes
            </span>
            <button
              className="focus-ring arrow-nudge inline-flex items-center gap-2 rounded-lg px-7 h-[52px] text-[16px] font-semibold transition-colors hover:bg-[rgba(31,29,24,0.07)]"
              style={{
                background: "transparent",
                color: "#1f1d18",
                border: "1.5px solid #1f1d18",
              }}
              onClick={next}
            >
              Get Started <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* FOOTER */}
          <div className="bleed px-9 pt-12 pb-8" style={{ background: "#1a1814" }}>
            <div className="max-w-[980px] mx-auto">
              <div className="grid grid-cols-[1.4fr_1fr_1fr] gap-8 pb-10">
                <div>
                  <div className="font-display text-[22px] text-white">VisaGenius</div>
                  <p
                    className="text-[12.5px] leading-relaxed mt-2 max-w-[280px]"
                    style={{ color: "rgba(255,253,247,0.55)" }}
                  >
                    Expert-built EB-1A petitions for researchers and professionals of
                    extraordinary ability. You petition for yourself — we make the case.
                  </p>
                </div>
                {([
                  ["Petition", ["Free assessment", "Evidence & criteria", "Letter studio", "Attorney review"]],
                  ["Company", ["About us", "Our attorneys", "Approved cases", "Contact"]],
                ] as const).map(([h, items]) => (
                  <div key={h}>
                    <div
                      className="font-sans text-[12.5px] uppercase tracking-[0.16em] mb-3"
                      style={{ color: "rgba(255,253,247,0.5)" }}
                    >
                      {h}
                    </div>
                    <div className="space-y-2">
                      {items.map((it) => (
                        <div
                          key={it}
                          className="text-[13px]"
                          style={{ color: "rgba(255,253,247,0.78)" }}
                        >
                          {it}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div
                className="font-display leading-none select-none"
                style={{
                  fontSize: "clamp(64px,14vw,150px)",
                  color: "rgba(255,253,247,0.08)",
                  letterSpacing: "-0.02em",
                }}
              >
                VisaGenius
              </div>
              <div
                className="flex items-center justify-between flex-wrap gap-3 pt-6 mt-2"
                style={{ borderTop: "1px solid rgba(255,253,247,0.12)" }}
              >
                <div
                  className="text-[12.5px]"
                  style={{ color: "rgba(255,253,247,0.45)" }}
                >
                  © 2026 VisaGenius · Privacy · Terms · Attorney advertising
                </div>
                <div className="flex items-center gap-3">
                  {[AtSign, Globe, Mail].map((I, i) => (
                    <span
                      key={i}
                      className="w-7 h-7 rounded-full flex items-center justify-center"
                      style={{
                        background: "rgba(255,253,247,0.1)",
                        color: "rgba(255,253,247,0.7)",
                      }}
                    >
                      <I className="w-3.5 h-3.5" />
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Footer indicator that this is the Locked + Secure feel */}
      <span className="sr-only">
        <Lock />
      </span>
    </div>
  );
}
